import {Injectable}                                  from '@angular/core';
import {
  ApDynGridStatusColumnConfig
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {nameOf}                                      from '../../../ap-core/utils/ap-name-of';
import {MonitoringChildGridItem, MonitoringGridItem} from '../../types/monitoring.types';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {GetRoundNumericPipe}                         from '../../../ap-utils';
import {
  DropDownButtonData
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridDetailsGridConfig
}                                                    from '../../../ap-dyngrids/config/details/ap-dyn-grid-details-grid-config';
import {
  ApDynGridPagerConfig
}                                                    from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  MonitoringFieldGridItem
}                                                    from '../../../entry-components/ap-field-management-entry-components/ap-monitoring-field/monitoring-field-entry.types';
import {map}                                         from 'rxjs/operators';
import {MapViewMode}                                 from '../../../ap-interface';
import {MapViewStore}                                from '../../../stores/layout/mapview.store';
import {
  GetRoundNumericPipeConfig
}                                                    from '../../../ap-utils/pipes/configs/get-round-numeric.pipe.config';
import {
  IGridStatus
}                                                    from '../../../ap-interface/interfaces/ap-grid-status.interface';
import ISettings = Data.BaseData.ISettings;
import ILanguage = Data.Language.ILanguage;

@Injectable()
export class MonitoringConfig {
  constructor(private mapViewStore: MapViewStore,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Configures main grid columns with monitoring filed information and actions.
   * @param burgerMenuButtons Array of menu button configurations.
   * @param setting Application settings for decimal definition.
   * @param language Language for date format definition.
   * @returns Array of column configurations.
   */
  public getMainGridColumns(burgerMenuButtons: DropDownButtonData[], setting: ISettings, language: ILanguage): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<MonitoringGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<MonitoringGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<MonitoringGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__NumberAbbr',
        field: nameOf<MonitoringGridItem>('FieldNumber'),
        hide: true,
        width: 40,
        cssClass: 'right',
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldPartShort',
        field: nameOf<MonitoringGridItem>('FieldSecondNumber'),
        hide: true,
        width: 40,
        cssClass: 'right',
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: nameOf<MonitoringFieldGridItem>('FieldCombinedNumber'),
        width: 70,
        hide: new ApDynGridColumnHideConfig({
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(
            map((mode) => mode === MapViewMode.HIDE)
          )
        }),
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldName',
        field: nameOf<MonitoringGridItem>('FieldName'),
        cssClass: 'left',
        width: 120,
        sortIndex: 0,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Crop',
        field: nameOf<MonitoringGridItem>('CropName'),
        width: 100,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'ECStageShort',
        field: nameOf<MonitoringGridItem>('EcValue'),
        width: 40,
        cssClass: 'right',
        template: PropertyTemplate.NUMBER,
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Area',
        field: nameOf<MonitoringGridItem>('Area'),
        width: 40,
        cssClass: 'right',
        hide: true,
        template: PropertyTemplate.NUMBER,
        tooltip: (dataItem: MonitoringGridItem) => dataItem?.Area?.toString(),
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [setting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'LastMeasurementDate',
        field: nameOf<MonitoringGridItem>('LatestMeasurementDate'),
        width: 80,
        hide: true,
        format: language.DateFormat,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'LastNApplicationDate',
        field: nameOf<MonitoringGridItem>('LatestNApplicationDate'),
        width: 80,
        hide: true,
        format: language.DateFormat,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'LastPpApplicationDate',
        field: nameOf<MonitoringGridItem>('LatestPpApplicationDate'),
        width: 80,
        hide: true,
        format: language.DateFormat,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridEditColumnConfig({
        data: burgerMenuButtons
      })
    ];
  }

  /**
   * Configures detail grid columns for monitoring details information.
   * @param burgerMenuButtons Array of menu button configurations.
   * @param setting Application settings for decimal definition.
   * @param language Language for date format definition.
   * @returns Detail grid configuration with columns.
   * @public
   */
  public getDetailsGridColumns(burgerMenuButtons: DropDownButtonData[], setting: ISettings, language: ILanguage): ApDynGridDetailsGridConfig {
    return new ApDynGridDetailsGridConfig({
      field: 'Details',
      checkboxDisabled: true,
      hide: (dataItem: MonitoringGridItem) => dataItem.Details.length <= 0,
      columns: [
        new ApDynGridStatusColumnConfig({
          field: `${nameOf<MonitoringChildGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
          class: `${nameOf<MonitoringChildGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
          description: `${nameOf<MonitoringChildGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
          filterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Docu_Ini__Date',
          field: nameOf<MonitoringChildGridItem>('Date'),
          width: 80,
          format: language.DateFormat,
          headerFilterable: true,
          filterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global_Type',
          field: nameOf<MonitoringChildGridItem>('TypeName'),
          width: 80,
          headerFilterable: true,
          filterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'ECStageShort',
          field: nameOf<MonitoringChildGridItem>('EcValue'),
          width: 40,
          cssClass: 'right',
          template: PropertyTemplate.NUMBER,
          filterable: true,
          filterType: FilterType.NUMBER,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'NMonitoring__MeasuredValue',
          field: nameOf<MonitoringChildGridItem>('Value'),
          width: 60,
          cssClass: 'right',
          filterable: true,
          filterType: FilterType.DATE,
          headerFilterable: true,
          pipes: [{
            pipe: this.roundNumericPipe,
            args: [setting.DigitsAfterDecimalPoint, GetRoundNumericPipeConfig.NullToEmptyReplaceConfig]
          }]
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Nutrients__Recommendation',
          field: nameOf<MonitoringChildGridItem>('Recommendation'),
          width: 60,
          cssClass: 'right',
          format: language.DateFormat,
          filterable: true,
          headerFilterable: true,
          pipes: [{
            pipe: this.roundNumericPipe,
            args: [setting.DigitsAfterDecimalPoint, GetRoundNumericPipeConfig.NullToEmptyReplaceConfig]
          }]
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__Note',
          field: nameOf<MonitoringChildGridItem>('Notes'),
          width: 80,
          filterable: true,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'FilesCount',
          field: nameOf<MonitoringChildGridItem>('FilesCount'),
          width: 40,
          filterable: true,
          pipes: [{
            pipe: this.roundNumericPipe,
            args: [0, GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig]
          }]
        }),
        new ApDynGridEditColumnConfig({
          data: burgerMenuButtons
        })
      ]
    });
  }

  /**
   * Creates basic pager configuration.
   * @returns Default grid pager configuration
   * @public
   */
  public getPager(): any {
    return new ApDynGridPagerConfig();
  }
}
